/**
 * API Client with automatic server failover and resilience for Render hosting.
 * 
 * Tries the primary backend server first. If it fails (network error, 5xx, timeout),
 * automatically retries the request on the backup server.
 * 
 * Uses localStorage to remember which server is healthy, so subsequent requests
 * go directly to the working server (with periodic re-checks of the primary).
 * 
 * Environment variables:
 *   REACT_APP_API_BASE_URL          — primary server (defaults to trackwicketbackend-fv5d)
 *   REACT_APP_API_BACKUP_URL        — backup server  (defaults to trackwicketbackend-kscl)
 */

const DEFAULT_PRIMARY = "https://trackwicketbackend-fv5d.onrender.com/api";
const DEFAULT_BACKUP = "https://trackwicketbackend-kscl.onrender.com/api";

function normalizeBaseUrl(url) {
  if (!url) return "";
  let trimmed = url.trim().replace(/\/+$/, "");
  if (!trimmed.endsWith("/api")) {
    trimmed = `${trimmed}/api`;
  }
  return trimmed;
}

const PRIMARY_URL = normalizeBaseUrl(process.env.REACT_APP_API_BASE_URL || DEFAULT_PRIMARY);
const BACKUP_URL = normalizeBaseUrl(process.env.REACT_APP_API_BACKUP_URL || DEFAULT_BACKUP);

const SERVERS = Array.from(new Set([PRIMARY_URL, BACKUP_URL].filter(Boolean)));

// Re-check previously failed primary server every 5 minutes
const RECHECK_INTERVAL_MS = 5 * 60 * 1000;
// Request timeout for health probes
const HEALTH_TIMEOUT_MS = 8000;
// Request timeout for normal API calls (25 seconds — accommodates Render cold starts & live scrapers)
const API_TIMEOUT_MS = 25000;

// ---------------------------------------------------------------------------
// Persistent state helpers (survives page reloads)
// ---------------------------------------------------------------------------
const STORAGE_KEY = "tw_active_server";
const STORAGE_TS_KEY = "tw_active_server_ts";

function getStoredActiveServer() {
  try {
    const url = localStorage.getItem(STORAGE_KEY);
    const ts = parseInt(localStorage.getItem(STORAGE_TS_KEY) || "0", 10);
    if (url && Date.now() - ts < RECHECK_INTERVAL_MS && SERVERS.includes(url)) {
      return url;
    }
  } catch {
    // localStorage may be restricted/unavailable
  }
  return null;
}

function setStoredActiveServer(url) {
  try {
    localStorage.setItem(STORAGE_KEY, url);
    localStorage.setItem(STORAGE_TS_KEY, String(Date.now()));
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// In-memory state
// ---------------------------------------------------------------------------
let activeServer = getStoredActiveServer() || PRIMARY_URL;

// In-memory cache for emergency fallback if network completely drops
const lastGoodResponses = new Map();

// ---------------------------------------------------------------------------
// Core: fetch with timeout
// ---------------------------------------------------------------------------
function fetchWithTimeout(url, options = {}, timeoutMs = API_TIMEOUT_MS) {
  const controller = new AbortController();
  const existingSignal = options.signal;

  if (existingSignal) {
    if (existingSignal.aborted) {
      controller.abort();
    } else {
      existingSignal.addEventListener("abort", () => controller.abort());
    }
  }

  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  return fetch(url, { ...options, signal: controller.signal }).finally(() =>
    clearTimeout(timeoutId)
  );
}

// ---------------------------------------------------------------------------
// Health check — lightweight probe
// ---------------------------------------------------------------------------
async function isServerHealthy(baseUrl) {
  try {
    // Probe /health on the base URL (which already ends with /api)
    const res = await fetchWithTimeout(
      `${baseUrl}/health`,
      { method: "GET" },
      HEALTH_TIMEOUT_MS
    );
    return res.ok;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// getActiveServer — returns the currently active base URL
// ---------------------------------------------------------------------------
export function getActiveServer() {
  return activeServer;
}

// ---------------------------------------------------------------------------
// resolveHealthyServer — probe all servers and pick the first healthy one
// ---------------------------------------------------------------------------
export async function resolveHealthyServer() {
  for (const server of SERVERS) {
    if (await isServerHealthy(server)) {
      activeServer = server;
      setStoredActiveServer(server);
      return server;
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// apiFetch — drop-in replacement for fetch() that adds automatic failover
// ---------------------------------------------------------------------------
/**
 * Make an API request with automatic failover between servers.
 *
 * @param {string} path - API path (e.g. `/live-cricket-scores`)
 * @param {RequestInit} [options] - Standard fetch options
 * @returns {Promise<Response>}
 */
export async function apiFetch(path, options = {}) {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  const orderedServers = getOrderedServers();

  let lastError = null;

  // Attempt each server in priority order
  for (const server of orderedServers) {
    try {
      const url = `${server}${cleanPath}`;
      const response = await fetchWithTimeout(url, options, API_TIMEOUT_MS);

      // Treat 5xx (500, 502, 503) as server failure worthy of failover
      if (response.status >= 500) {
        lastError = new Error(`Server ${server} returned HTTP ${response.status}`);
        console.warn(`[apiClient] ${server}${cleanPath} returned ${response.status}. Trying failover...`);
        continue;
      }

      // Success — remember this server as healthy
      if (server !== activeServer) {
        console.log(`[apiClient] ✅ Switched active server to: ${server}`);
        activeServer = server;
        setStoredActiveServer(server);
      }

      return response;
    } catch (err) {
      lastError = err;
      console.warn(`[apiClient] ⚠️ ${server}${cleanPath} failed:`, err.message);
    }
  }

  // If first round failed across all servers, perform a single 1.5s delayed retry
  // (Crucial for cold-starting Render instances)
  if (orderedServers.length > 0) {
    await new Promise((r) => setTimeout(r, 1500));
    for (const server of orderedServers) {
      try {
        const url = `${server}${cleanPath}`;
        const response = await fetchWithTimeout(url, options, API_TIMEOUT_MS);
        if (response.status < 500) {
          activeServer = server;
          setStoredActiveServer(server);
          return response;
        }
      } catch (retryErr) {
        lastError = retryErr;
      }
    }
  }

  // Emergency fallback to cached response if available
  if (lastGoodResponses.has(cleanPath)) {
    console.warn(`[apiClient] Returning cached emergency response for ${cleanPath}`);
    return new Response(JSON.stringify(lastGoodResponses.get(cleanPath)), {
      status: 200,
      headers: { "Content-Type": "application/json" }
    });
  }

  throw lastError || new Error("All backend servers are unreachable");
}

// ---------------------------------------------------------------------------
// apiFetchJSON — convenience wrapper that parses JSON and updates emergency cache
// ---------------------------------------------------------------------------
export async function apiFetchJSON(path, options = {}) {
  const response = await apiFetch(path, options);
  const data = await response.json();
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  if (data && data.status === "success") {
    lastGoodResponses.set(cleanPath, data);
  }
  return data;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function getOrderedServers() {
  if (SERVERS.length <= 1) return SERVERS;
  return [activeServer, ...SERVERS.filter((s) => s !== activeServer)];
}

// ---------------------------------------------------------------------------
// checkAllServers — used by ServerMonitor
// ---------------------------------------------------------------------------
export async function checkAllServers() {
  const results = await Promise.all(
    SERVERS.map(async (server) => ({
      server,
      healthy: await isServerHealthy(server),
    }))
  );

  const healthy = results.find((r) => r.healthy);
  if (healthy) {
    activeServer = healthy.server;
    setStoredActiveServer(healthy.server);
  }

  return {
    allDown: !healthy,
    results,
    activeServer: healthy ? healthy.server : null,
  };
}

export default apiFetch;
